import React, { useState } from 'react';
import { Users, MapPin, TrendingUp, Wallet, RefreshCw, FileText, Download, Printer, AlertTriangle } from 'lucide-react';
import { StatsCard } from './StatsCard';
import { DashboardStats, Town, Member, Contribution, Event } from '../../types';
import { RecentActivity } from './RecentActivity';
import { QuickActions } from './QuickActions';
import { PermissionGate } from '../Common/PermissionGate';
import { PERMISSIONS } from '../../utils/permissions';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { exportMembersReportPDF, exportFinancialReportPDF } from '../../utils/pdfExport';
import { exportToExcel, exportMembersReportExcel, exportFinancialReportExcel } from '../../utils/excel';
import { EnhancedDashboard } from './EnhancedDashboard';
import { AnalyticsProvider } from './AdvancedAnalyticsProvider';
import { HelpButton } from '../Help/HelpButton';

interface DashboardProps {
  stats: DashboardStats;
  selectedTownId: string;
  towns: Town[];
  members: Member[];
  contributions: Contribution[];
  events: Event[];
  onQuickAction: (action: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

interface ExportLoadingState {
  members: boolean;
  financial: boolean;
  excel: boolean;
}

export const Dashboard: React.FC<DashboardProps> = ({
  stats,
  selectedTownId,
  towns,
  members,
  contributions,
  events,
  onQuickAction,
  onRefresh,
  isRefreshing = false
}) => {
  const [exportLoading, setExportLoading] = useState<ExportLoadingState>({
    members: false,
    financial: false,
    excel: false
  });

  const [useAdvancedAnalytics, setUseAdvancedAnalytics] = useState(false);

  // Export handlers
  const handleExportMembersPDF = async () => {
    setExportLoading(prev => ({ ...prev, members: true }));
    try {
      await exportMembersReportPDF(members, towns, selectedTownId);
    } catch (error) {
      console.error('Failed to export members PDF:', error);
      // In a real app, you would show a toast notification here
    } finally {
      setExportLoading(prev => ({ ...prev, members: false }));
    }
  };

  const handleExportFinancialPDF = async () => {
    setExportLoading(prev => ({ ...prev, financial: true }));
    try {
      await exportFinancialReportPDF(contributions, [], towns, selectedTownId);
    } catch (error) {
      console.error('Failed to export financial PDF:', error);
      // In a real app, you would show a toast notification here
    } finally {
      setExportLoading(prev => ({ ...prev, financial: false }));
    }
  };

  const handleExportMembersExcel = async () => {
    setExportLoading(prev => ({ ...prev, members: true }));
    try {
      await exportMembersReportExcel(members, towns, selectedTownId);
    } catch (error) {
      console.error('Failed to export members Excel:', error);
    } finally {
      setExportLoading(prev => ({ ...prev, members: false }));
    }
  };

  const handleExportFinancialExcel = async () => {
    setExportLoading(prev => ({ ...prev, financial: true }));
    try {
      await exportFinancialReportExcel(contributions, [], towns, selectedTownId);
    } catch (error) {
      console.error('Failed to export financial Excel:', error);
    } finally {
      setExportLoading(prev => ({ ...prev, financial: false }));
    }
  };

  const handleExportExcel = () => {
    setExportLoading(prev => ({ ...prev, excel: true }));
    try {
      // Export summary data to Excel
      const summaryData = [{
        'Total Members': stats.totalMembers,
        'Total Revenue': stats.totalRevenue,
        'Current Balance': stats.currentBalance,
        'Member Growth': stats.memberGrowth,
        'Revenue Growth': stats.revenueGrowth,
        'Selected Town': selectedTownName,
        'Generated': new Date().toLocaleDateString()
      }];

      exportToExcel(summaryData, `Dashboard_Summary_${selectedTownName}_${new Date().toISOString().slice(0, 10)}`);
    } catch (error) {
      console.error('Failed to export Excel:', error);
    } finally {
      setExportLoading(prev => ({ ...prev, excel: false }));
    }
  };

  const selectedTownName = (() => {
    if (selectedTownId === 'no-town-assigned') {
      return 'No Town Assigned';
    }
    if (selectedTownId === 'all') {
      return 'All Towns';
    }
    return towns.find(t => t.id === selectedTownId)?.name || 'Unknown Town';
  })();

  if (useAdvancedAnalytics) {
    return (
      <AnalyticsProvider>
        <EnhancedDashboard onBackToDashboard={() => setUseAdvancedAnalytics(false)} />
      </AnalyticsProvider>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 dark:text-dark-text-primary">
            Dashboard - {selectedTownName}
          </h1>
          <p className="text-gray-600 dark:text-dark-text-secondary mt-1">
            Overview of family membership and financial data
            {selectedTownId !== 'all' && ' for ' + selectedTownName}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setUseAdvancedAnalytics(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <TrendingUp className="h-4 w-4" />
            <span>Advanced Analytics</span>
          </button>

          {onRefresh && (
            <button
              onClick={onRefresh}
              disabled={isRefreshing}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              <span>Refresh</span>
            </button>
          )}

          <HelpButton guideId="dashboard/dashboard-overview" variant="button" />

          <PermissionGate permission={PERMISSIONS.VIEW_MEMBERS}>
            <button
              onClick={handleExportMembersPDF}
              disabled={exportLoading.members}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <FileText className={`h-4 w-4 ${exportLoading.members ? 'animate-spin' : ''}`} />
              <span>Export Members PDF</span>
            </button>
            <button
              onClick={handleExportMembersExcel}
              disabled={exportLoading.members}
              className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Download className={`h-4 w-4 ${exportLoading.members ? 'animate-spin' : ''}`} />
              <span>Export Members Excel</span>
            </button>
          </PermissionGate>

          <PermissionGate permission={PERMISSIONS.VIEW_FINANCES}>
            <button
              onClick={handleExportFinancialPDF}
              disabled={exportLoading.financial}
              className="bg-purple-600 hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <FileText className={`h-4 w-4 ${exportLoading.financial ? 'animate-spin' : ''}`} />
              <span>Export Financial PDF</span>
            </button>
            <button
              onClick={handleExportFinancialExcel}
              disabled={exportLoading.financial}
              className="bg-orange-600 hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
            >
              <Download className={`h-4 w-4 ${exportLoading.financial ? 'animate-spin' : ''}`} />
              <span>Export Financial Excel</span>
            </button>
          </PermissionGate>

          <button
            onClick={handleExportExcel}
            disabled={exportLoading.excel}
            className="bg-gray-600 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-4 py-2 rounded-lg flex items-center space-x-2 transition-colors"
          >
            <Download className={`h-4 w-4 ${exportLoading.excel ? 'animate-spin' : ''}`} />
            <span>Export Summary Excel</span>
          </button>
        </div>
      </div>

      {(stats.pendingApprovals ?? 0) > 0 && (
        <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="bg-yellow-100 dark:bg-yellow-800 p-2 rounded-full">
              <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400" />
            </div>
            <div>
              <h3 className="font-medium text-yellow-900 dark:text-yellow-200">Pending Approvals</h3>
              <p className="text-sm text-yellow-700 dark:text-yellow-300">
                There are {stats.pendingApprovals} items waiting for your approval.
              </p>
            </div>
          </div>
          <button
            onClick={() => onQuickAction('approvals')}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
          >
            Review Items
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <PermissionGate permission={PERMISSIONS.VIEW_MEMBERS}>
          <StatsCard
            title="Total Members"
            value={stats.totalMembers}
            change={stats.memberGrowth}
            icon={Users}
            type="number"
            color="blue"
            onClick={() => onQuickAction('members')}
          />
        </PermissionGate>

        <PermissionGate permission={PERMISSIONS.MANAGE_USERS}>
          <StatsCard
            title={selectedTownId === 'all' ? 'Total Towns' : 'Town Revenue'}
            value={selectedTownId === 'all' ? stats.totalTowns : stats.totalRevenue}
            change={selectedTownId === 'all' ? stats.townGrowth : stats.revenueGrowth}
            icon={selectedTownId === 'all' ? MapPin : TrendingUp}
            type={selectedTownId === 'all' ? 'number' : 'currency'}
            color="green"
          />
        </PermissionGate>

        <PermissionGate permission={PERMISSIONS.VIEW_FINANCES}>
          <StatsCard
            title="Total Revenue"
            value={stats.totalRevenue}
            change={stats.revenueGrowth}
            icon={TrendingUp}
            type="currency"
            color="purple"
            onClick={() => onQuickAction('contributions')}
          />
        </PermissionGate>

        <PermissionGate permission={PERMISSIONS.VIEW_FINANCES}>
          <StatsCard
            title="Current Balance"
            value={stats.currentBalance}
            change={stats.balanceChange}
            icon={Wallet}
            type="currency"
            color="orange"
          />
        </PermissionGate>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        <div className="lg:col-span-2">
          <div className="bg-white dark:bg-dark-bg-secondary rounded-lg shadow p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-dark-text-primary mb-4">
              Revenue Trend
            </h3>
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm text-gray-600 dark:text-gray-300">
                Switch to Advanced Analytics for interactive charts
              </span>
              <button
                onClick={() => setUseAdvancedAnalytics(true)}
                className="text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 text-sm font-medium"
              >
                View Advanced Charts →
              </button>
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={[
                { month: 'Jan', revenue: stats.totalRevenue * 0.7 },
                { month: 'Feb', revenue: stats.totalRevenue * 0.8 },
                { month: 'Mar', revenue: stats.totalRevenue * 0.9 },
                { month: 'Apr', revenue: stats.totalRevenue }
              ]}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip />
                <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <RecentActivity
            members={members}
            contributions={contributions}
            events={events}
            towns={towns}
            selectedTownId={selectedTownId}
          />
        </div>
        <div>
          <QuickActions onAction={onQuickAction} />
        </div>
      </div>
    </div>
  );
};